import api from "@/lib/api";

/* Web Push helper — subscribe/unsubscribe via the existing service worker. */

export function isPushSupported() {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function permissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getReadyRegistration() {
  // Ensure the SW is registered, then wait for it to be ready.
  if (!navigator.serviceWorker.controller) {
    try { await navigator.serviceWorker.register("/sw.js"); } catch (e) { /* already registered */ }
  }
  return navigator.serviceWorker.ready;
}

async function fetchVapidKey() {
  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.enabled || !data?.public_key) {
    const err = new Error("disabled");
    err.code = "disabled";
    throw err;
  }
  return data.public_key;
}

async function ensureBrowserSubscription(publicKey) {
  const reg = await getReadyRegistration();
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    const err = new Error("denied");
    err.code = perm === "denied" ? "denied" : "dismissed";
    throw err;
  }
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  return sub;
}

/** Subscribe the current (authenticated) member. */
export async function subscribePush() {
  if (!isPushSupported()) {
    const err = new Error("unsupported");
    err.code = "unsupported";
    throw err;
  }
  const publicKey = await fetchVapidKey();
  const sub = await ensureBrowserSubscription(publicKey);
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

/** Unsubscribe the current member on this device. */
export async function unsubscribePush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch (e) { /* noop */ }
    try { await api.post("/push/unsubscribe", { endpoint }); } catch (e) { /* noop */ }
  }
}

/** Subscribe an anonymous green-room guest, keyed by their join request. */
export async function subscribeGuestPush(inviteToken, requestId) {
  if (!isPushSupported()) {
    const err = new Error("unsupported");
    err.code = "unsupported";
    throw err;
  }
  const publicKey = await fetchVapidKey();
  const sub = await ensureBrowserSubscription(publicKey);
  await api.post("/push/guest/subscribe", {
    invite_token: inviteToken,
    request_id: requestId,
    subscription: sub.toJSON(),
  });
  return sub;
}

export async function getPushStatus() {
  const { data } = await api.get("/push/status");
  return data; // { enabled, subscribed, devices }
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
